// frontend/src/pages/AuthCallback.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { pushRedditConversionEvent } from "../utils/redditTracking";

const AUTH_PARAM_KEYS = [
  "access_token",
  "refresh_token",
  "id_token",
  "expires_at",
  "expires_in",
  "token_type",
  "type",
  "code",
  "state",
  "error",
  "error_description",
];

const collectAuthParams = () => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const searchParams = new URLSearchParams(window.location.search);

  // Merge search params into hash params (prefer hash if duplicates exist)
  searchParams.forEach((value, key) => {
    if (!hashParams.has(key)) hashParams.set(key, value);
  });

  return hashParams;
};

const clearAuthParams = () => {
  const url = new URL(window.location.href);

  // Clear from ?query
  AUTH_PARAM_KEYS.forEach((key) => url.searchParams.delete(key));

  // Clear from #hash
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  AUTH_PARAM_KEYS.forEach((key) => hashParams.delete(key));

  const newHash = hashParams.toString();
  const newSearch = url.searchParams.toString();

  const cleanedUrl = `${url.origin}${url.pathname}${newSearch ? `?${newSearch}` : ""}${
    newHash ? `#${newHash}` : ""
  }`;

  window.history.replaceState({}, document.title, cleanedUrl);
};

const getFormattedDomain = (domain, region) => {
  if (!domain) return "";
  if (domain.endsWith("amazoncognito.com")) return domain;
  if (domain.includes(".")) return domain;
  return `${domain}.auth.${region}.amazoncognito.com`;
};

const getStoredUserEmail = () => {
  try {
    const raw =
      localStorage.getItem("snapgrad_user") || localStorage.getItem("userData");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.email || "";
  } catch {
    return "";
  }
};

const maybeTrackGoogleSuccess = () => {
  if (typeof window === "undefined") return;
  if (window.sessionStorage?.getItem("reddit_google_success_tracked") === "true") {
    return;
  }

  const email = getStoredUserEmail();
  pushRedditConversionEvent({
    event: "sign_up_google_success",
    userData: email ? { email_address: email } : undefined,
    eventData: {
      action: "signup",
      source: "google_oauth_success",
    },
  });

  try {
    window.sessionStorage?.setItem("reddit_google_success_tracked", "true");
  } catch {}
};

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();

  const [status, setStatus] = useState("Processing authentication...");
  const [error, setError] = useState(null);

  const resolveRedirectTarget = (rawState) => {
    if (!rawState) return "/";
    if (rawState.startsWith("/")) return rawState;
    try {
      const url = new URL(rawState, window.location.origin);
      if (url.origin === window.location.origin) {
        return `${url.pathname}${url.search}${url.hash}`;
      }
    } catch {}
    return "/";
  };

  const navigateToApp = (target) => {
    const resolvedTarget = resolveRedirectTarget(target);
    try {
      navigate(resolvedTarget, { replace: true });
    } catch {}

    // iOS Safari can ignore SPA navigation after OAuth; force reload if needed.
    setTimeout(() => {
      if (window.location.pathname.startsWith("/auth/callback")) {
        window.location.replace(resolvedTarget);
      }
    }, 500);
  };

  const getCachedTokens = () => {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    if (!clientId) return null;
    const key = `CognitoIdentityServiceProvider.${clientId}.LastAuthResult`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.accessToken && parsed?.idToken) return parsed;
    } catch {
      return null;
    }
    return null;
  };

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const params = collectAuthParams();

      const oauthError = params.get("error");
      const oauthErrorDesc = params.get("error_description") || "";

      if (oauthError) {
        if (!alive) return;
        setError(
          `Cognito returned an error: ${oauthError}${
            oauthErrorDesc ? ` (${oauthErrorDesc})` : ""
          }`
        );
        return;
      }

      const code = params.get("code");
      const state = params.get("state");
      const redirectTarget = state || "/";

      // If we already have cached tokens (e.g., double-callback or previous success), trust them
      const cached = getCachedTokens();
      if (cached) {
        clearAuthParams();
        try {
          await refreshSession();
          maybeTrackGoogleSuccess();
          if (!alive) return;
          setStatus("Success! Redirecting you to SnapGrad...");
          setTimeout(() => navigateToApp(redirectTarget), 300);
          return;
        } catch {
          // fall through to normal flow if refresh failed
        }
      }

      // If nothing returned, just go home
      if (!code) {
        clearAuthParams();
        if (!alive) return;
        setStatus("No login response found. Redirecting...");
        setTimeout(() => navigateToApp("/"), 300);
        return;
      }

      // Exchange code -> tokens directly with Cognito
      let clientId;
      try {
        if (!alive) return;
        setStatus("Completing sign-in...");

        const region = process.env.REACT_APP_COGNITO_REGION;
        clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
        const domainRaw = process.env.REACT_APP_COGNITO_DOMAIN;
        const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;

        const domain = getFormattedDomain(domainRaw, region);
        if (!domain || !clientId || !redirectUri) {
          throw new Error("Missing Cognito env vars (domain/clientId/redirectUri).");
        }

        const tokenUrl = `https://${domain}/oauth2/token`;

        const body = new URLSearchParams();
        body.set("grant_type", "authorization_code");
        body.set("client_id", clientId);
        body.set("code", code);
        body.set("redirect_uri", redirectUri);

        const resp = await fetch(tokenUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });

        if (!resp.ok) {
          const txt = await resp.text().catch(() => "");
          throw new Error(txt || `Token exchange failed (${resp.status})`);
        }

        const tokens = await resp.json();

        // Store tokens in the same localStorage format your AuthProvider fallback expects
        const lastAuthResultKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthResult`;
        localStorage.setItem(
          lastAuthResultKey,
          JSON.stringify({
            accessToken: tokens.access_token,
            idToken: tokens.id_token,
            refreshToken: tokens.refresh_token || "",
            clockDrift: 0,
          })
        );

      // Clean callback URL + refresh UI auth state
      clearAuthParams();
      await refreshSession().catch(() => {});
      maybeTrackGoogleSuccess();

      if (!alive) return;
      setStatus("Success! Redirecting you to SnapGrad...");
      setTimeout(() => navigateToApp(redirectTarget), 400);
      } catch (e) {
        console.error("[AuthCallback] Error:", e);

        let recovered = false;

        // If tokens are already present (e.g., first attempt succeeded and a second exchange hit invalid_grant),
        // treat this as success and move on without flashing an error.
        if (clientId) {
          const lastAuthResultKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthResult`;
          const raw = localStorage.getItem(lastAuthResultKey);
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              if (parsed?.accessToken && parsed?.idToken) {
                recovered = true;
              }
            } catch {
              // ignore
            }
          }
        }

        // As a backup, try to refresh the session directly; if it succeeds, proceed silently.
        if (!recovered) {
          try {
            await refreshSession();
            recovered = true;
          } catch {
            recovered = false;
          }
        }

        if (recovered) {
          clearAuthParams();
          maybeTrackGoogleSuccess();
          if (!alive) return;
          setStatus("Success! Redirecting you to SnapGrad...");
          setTimeout(() => navigateToApp(redirectTarget), 400);
          return;
        }

        clearAuthParams();
        if (!alive) return;
        setError(`Could not complete Cognito authentication: ${e?.message || String(e)}`);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [navigate, refreshSession]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#F5F3F1] px-4">
      <div className="w-full max-w-md p-8 space-y-4 bg-white rounded-lg shadow-lg text-center">
        <h1 className="text-2xl font-semibold text-gray-900">Authentication</h1>
        {error ? (
          <>
            <p className="text-red-600 text-sm">{error}</p>
            <p className="text-sm text-gray-600">
              You can safely close this tab and try logging in again from the SnapGrad app.
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-700">{status}</p>
            <p className="text-sm text-gray-500">This will only take a second.</p>
          </>
        )}
      </div>
    </div>
  );
}
